import React, { useState, useEffect } from "react";
import {
  ListItem,
  Stack,
  Avatar,
  ListItemText,
  ListItemButton,
  IconButton,
  Typography,
  Tooltip,
  Box,
  Badge,
} from "@mui/material";
import { UserCircle, BellRinging, BellSimpleSlash } from "@phosphor-icons/react";
import getAvatarInitials from "utils/getAvatarInitials";
import { useTheme } from "@mui/material/styles";
import { formatMessageTimestamp } from "utils/formatMessageTimestamp";

const MessagePreviewItem = ({
  preview,
  onSelectChat,
  openChatId,
  recentlyReadChats,
  externalNotificationRefetch,
  userId,
  updateParticipantMutation,
  refetchPreviews,
  selectedTeamMemberId,
}) => {
  const theme = useTheme();

  const [pending, setPending] = useState(false);
  const [localNotifState, setLocalNotifState] = useState(preview.notifications_enabled);

  const placeholderText = `${preview.sender_first_name} sent a file`;
  const usePlaceholderText = !preview.last_message && preview.last_message_metadata?.s3Key;

  useEffect(() => {
    setLocalNotifState(preview.notifications_enabled);
  }, [preview.notifications_enabled]);

  const handleToggle = async (e) => {
    e.stopPropagation(); // prevent click from selecting chat
    if (pending) return;

    setPending(true);
    setLocalNotifState(!preview.notifications_enabled);
    updateParticipantMutation(
      {
        user_id: userId,
        chat_id: preview.chat_id,
        update_data: {
          notifications_enabled: !preview.notifications_enabled,
        },
      },
      {
        onSuccess: () => {
          refetchPreviews();
          setPending(false);
          if (selectedTeamMemberId) {
            externalNotificationRefetch?.();
          }
        },
        onError: () => {
          setPending(false);
        },
      }
    );
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => onSelectChat?.(preview.chat_id)}
        sx={{
          borderRadius: "12px",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Badge
          variant="dot"
          color="warning"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          invisible={
            preview.user_has_read ||
            openChatId === preview.chat_id ||
            recentlyReadChats?.has(preview.chat_id)
          }
          sx={{
            mr: "12px",
            "& .MuiBadge-badge": {
              border: "2px solid white",
              borderRadius: "100px",
              width: "10px",
              height: "10px",
              transform: "translate(15%, 10%)",
            },
          }}
        >
          <Avatar src={preview.imageUrl} sx={{ width: 32, height: 32 }}>
            {getAvatarInitials(preview.member_full_name)}
          </Avatar>
        </Badge>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            position: "absolute",
            top: "50%",
            right: 0,
            transform: "translateY(-50%)",
            height: "100%",
            width: "48px",
            pointerEvents: "none",
            background: `linear-gradient(to left, ${theme.palette.action.hover} 70%, transparent)`,
            borderRadius: "12px",
            opacity: 0,
            transition: "opacity 150ms ease-in-out",
            ".MuiListItemButton-root:hover &": {
              opacity: 1,
              pointerEvents: "auto",
            },
          }}
        >
          <Tooltip title={localNotifState ? "Turn off notifications" : "Notify me"} placement="top">
            <IconButton
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleToggle}
            >
              {localNotifState ? (
                <BellSimpleSlash weight="duotone" size={18} color={theme.palette.text.secondary} />
              ) : (
                <BellRinging weight="duotone" size={18} color={theme.palette.text.secondary} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>

        <ListItemText
          primary={
            <Stack direction="row" alignItems="center" width="100%" gap="12px">
              <Typography
                variant="subtitle1"
                sx={{
                  maxWidth: 165,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {preview.member_full_name}
              </Typography>
              {preview.last_message_date && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {formatMessageTimestamp(preview.last_message_date)}
                </Typography>
              )}
              {preview.teams?.length > 1 ? (
                <Stack direction="row" gap="4px" alignItems="center" sx={{ ml: "auto" }}>
                  <UserCircle
                    fontSize="16px"
                    weight="duotone"
                    color={theme.palette.text.secondary}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {preview.teams?.length}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    ml: "auto",

                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {preview.teams?.[0]?.clientName || ""}
                </Typography>
              )}
            </Stack>
          }
          secondary={
            <Box sx={{ overflow: "hidden" }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {usePlaceholderText ? placeholderText : preview.last_message}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};

export default MessagePreviewItem;
